/**
 * Main application layout
 */

import React from 'react';
import {
  AppBar,
  Avatar,
  Box,
  IconButton,
  Toolbar,
  Tooltip,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import {
  Logout as LogoutIcon,
  DarkMode as DarkModeIcon,
  LightMode as LightModeIcon,
} from '@mui/icons-material';
import { useAuthStore, useThemeStore } from '../../core/store';
import authService from 'core/services/auth.service';
import { ROUTES } from '../../core/constants';
import {
  logAmplitudeEventWithPayload,
  LOGOUT_EVENT,
  THEME_TOGGLED_EVENT,
} from 'core/amplitude/amplitude';

interface AppLayoutProps {
  children: React.ReactNode;
}

const AppLayout: React.FC<AppLayoutProps> = ({ children }) => {
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();
  const { mode, toggleTheme } = useThemeStore();

  const handleLogout = async () => {
    try {
      logAmplitudeEventWithPayload(LOGOUT_EVENT, { app: 'AuditLogs' });

      // Fire backend logout (don't block navigation on failure)
      authService.logout().catch(() => {});

      // Clear client caches first to avoid flicker
      localStorage.clear();
      sessionStorage.clear();

      // Reset stores
      logout();

      // Navigate to login
      navigate(ROUTES.LOGIN);
    } catch (e) {
      // Fallback navigation even if clearing throws
      navigate(ROUTES.LOGIN);
    }
  };

  const handleThemeToggle = () => {
    const toTheme = mode === 'light' ? 'dark' : 'light';
    logAmplitudeEventWithPayload(THEME_TOGGLED_EVENT, {
      app: 'AuditLogs',
      from_theme: mode,
      to_theme: toTheme,
    });
    toggleTheme();
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
      <AppBar
        position="static"
        color="default"
        elevation={0}
        sx={{ borderBottom: 1, borderColor: 'divider' }}
      >
        <Toolbar sx={{ minHeight: 56 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              component="img"
              src={'/aspen-logo-normal-white.png'}
              alt="Aspen Audit Logs"
              sx={{ height: 45, width: 148 }}
            />
          </Box>
          <Box sx={{ flex: 1 }} />
          <Tooltip
            title={
              mode === 'light' ? 'Switch to dark mode' : 'Switch to light mode'
            }
          >
            <IconButton onClick={handleThemeToggle} color="inherit">
              {mode === 'light' ? <DarkModeIcon /> : <LightModeIcon />}
            </IconButton>
          </Tooltip>
          <Box
            sx={{ display: 'flex', alignItems: 'center', gap: 1.5, ml: 1.5 }}
          >
            {user?.userName && (
              <>
                <Tooltip title={user.userName}>
                  <Avatar
                    sx={{
                      width: 28,
                      height: 28,
                      fontSize: 14,
                    }}
                  >
                    {user.userName.substring(0, 1).toUpperCase()}
                  </Avatar>
                </Tooltip>
              </>
            )}
            <Tooltip title="Logout">
              <IconButton onClick={handleLogout} color="inherit">
                <LogoutIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Toolbar>
      </AppBar>

      <Box sx={{ flex: 1, overflow: 'auto' }}>{children}</Box>
    </Box>
  );
};

export default AppLayout;
